import { Injectable } from "@angular/core";
import { TableColumnDetails } from "../model/tableColumnDetails";
import { TableColumn } from "../model/tableColumn";
import { HttpHeaders, HttpParameterCodec } from "@angular/common/http";
import { ColDef } from "ag-grid-enterprise";
import { AgHeaderRendererComponent } from "@app/modules/add-view-release-list/ag-header-renderer/ag-header-renderer.component";
import { AgChipRendererComponent } from "@app/modules/add-view-release-list/ag-chip-renderer/ag-chip-renderer.component";
import moment from "moment";
import { ConstantsService } from "@app/common/constants.service";
import { UserAuthService } from "@app/shared/services/user-auth.service";

@Injectable({
  providedIn: "root",
})
export class AgGridConfigurationService {
  defaultColDef = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
    headerCheckboxSelection: false,
    checkboxSelection: false,
    rowSelection: "multiple",
    suppressRowClickSelection: true
  };
  rowModelType = "serverSide";
  serverSideStoreType = 'partial';
  color1: string = "";
  color2: string = "";
  defaultAgGridTheme = "ag-theme-alpine";
  paginationOn = true;
  paginationPageSize10 = 10;
  public defaultHeaders = new HttpHeaders();
  columnInfo: ColDef;
  public encoder: HttpParameterCodec;
  agGridLocalConfig: Record<string, string>;
  constructor(public constantsService: ConstantsService, public userAuthService: UserAuthService) { }
  changeRowColor(color1: string, color2: string): void {
    this.color1 = color1;
    this.color2 = color2;
  }
  getAgGridConfig(): Record<string, string> {
    // Set the locale text for ag grid with static values
    return this.agGridLocalConfig = {
      selectAll: "Select All",
      selectAllSearchResults: "Select All Search Results",
      addCurrentSelectionToFilter: "Add Current Selection to Filter",
      searchOoo: "Search...",
      blanks: "Blanks",
      noMatches: "No Matches",
      filterOoo: "Filter...",
      equals: "Equals",
      notEqual: "Not Equal",
      blank: "Blank",
      notBlank: "Not Blank",
      empty: "Empty",
      lessThan: "Less Than",
      greaterThan: "Greater Than",
      lessThanOrEqual: "Less Than Or Equal",
      greaterThanOrEqual: "Greater Than Or Equal",
      inRange: "In Range",
      inRangeStart: "Range Start",
      inRangeEnd: "Range End",
      contains: "Contains",
      notContains: "Not Contains",
      startsWith: "Starts With",
      endsWith: "Ends With",
      dateFormatOoo: "yyyy-mm-dd",
      before: "Before",
      after: "After",
      andCondition: "AND",
      orCondition: "OR",
      applyFilter: "Apply Filter",
      resetFilter: "Reset Filter",
      clearFilter: "Clear Filter",
      cancelFilter: "Cancel",
      textFilter: "Text Filter",
      numberFilter: "Number Filter",
      dateFilter: "Date Filter",
      setFilter: "Set Filter",
      groupFilterSelect: "Select Group",
      advancedFilterContains: "Contains",
      advancedFilterNotContains: "Does Not Contain",
      advancedFilterTextEquals: "Equals",
      advancedFilterTextNotEqual: "Not Equal",
      advancedFilterStartsWith: "Starts With",
      advancedFilterEndsWith: "Ends With",
      advancedFilterBlank: "Blank",
      advancedFilterNotBlank: "Not Blank",
      advancedFilterEquals: "Equals",
      advancedFilterNotEqual: "Not Equal",
      advancedFilterGreaterThan: "Greater Than",
      advancedFilterGreaterThanOrEqual: "Greater Than Or Equal",
      advancedFilterLessThan: "Less Than",
      advancedFilterLessThanOrEqual: "Less Than Or Equal",
      advancedFilterTrue: "True",
      advancedFilterFalse: "False",
      advancedFilterAnd: "AND",
      advancedFilterOr: "OR",
      advancedFilterApply: "Apply",
      advancedFilterBuilder: "Advanced Filter Builder",
      advancedFilterValidationMissingColumn: "Missing Column",
      advancedFilterValidationMissingOption: "Missing Option",
      advancedFilterValidationMissingValue: "Missing Value",
      advancedFilterValidationInvalidColumn: "Invalid Column",
      advancedFilterValidationInvalidOption: "Invalid Option",
      advancedFilterValidationMissingQuote: "Missing Quote",
      advancedFilterValidationNotANumber: "Not a Number",
      advancedFilterValidationInvalidDate: "Invalid Date",
      advancedFilterValidationMissingCondition: "Missing Condition",
      advancedFilterValidationJoinOperatorMismatch: "Join Operator Mismatch",
      advancedFilterValidationInvalidJoinOperator: "Invalid Join Operator",
      advancedFilterValidationMissingEndBracket: "Missing End Bracket",
      advancedFilterValidationExtraEndBracket: "Extra End Bracket",
      advancedFilterValidationMessage: "Validation Message",
      advancedFilterValidationMessageAtEnd: "Validation Message At End",
      advancedFilterBuilderTitle: "Advanced Filter Builder",
      advancedFilterBuilderApply: "Apply",
      advancedFilterBuilderCancel: "Cancel",
      advancedFilterBuilderAddButtonTooltip: "Add",
      advancedFilterBuilderRemoveButtonTooltip: "Remove",
      advancedFilterBuilderMoveUpButtonTooltip: "Move Up",
      advancedFilterBuilderMoveDownButtonTooltip: "Move Down",
      advancedFilterBuilderAddJoin: "Add Join",
      advancedFilterBuilderAddCondition: "Add Condition",
      advancedFilterBuilderSelectColumn: "Select Column",
      advancedFilterBuilderSelectOption: "Select Option",
      advancedFilterBuilderEnterValue: "Enter Value",
      advancedFilterBuilderValidationAlreadyApplied: "Already Applied",
      advancedFilterBuilderValidationIncomplete: "Incomplete",
      advancedFilterBuilderValidationSelectColumn: "Select Column",
      advancedFilterBuilderValidationSelectOption: "Select Option",
      advancedFilterBuilderValidationEnterValue: "Enter Value",
      columns: "Columns",
      filters: "Filters",
      pivotMode: "Pivot Mode",
      groups: "Groups",
      rowGroupColumnsEmptyMessage: "Drag here to set row groups",
      values: "Values",
      valueColumnsEmptyMessage: "Drag here to aggregate",
      pivots: "Pivots",
      pivotColumnsEmptyMessage: "Drag here to pivot",
      group: "Group",
      rowDragRow: "row",
      rowDragRows: "rows",
      loadingOoo: "Loading...",
      loadingError: "Error Loading",
      noRowsToShow: "No Rows To Show",
      enabled: "Enabled",
      pinColumn: "Pin Column",
      pinLeft: "Pin Left",
      pinRight: "Pin Right",
      noPin: "No Pin",
      valueAggregation: "Value Aggregation",
      noAggregation: "No Aggregation",
      autosizeThiscolumn: "Autosize This Column",
      autosizeAllColumns: "Autosize All Columns",
      groupBy: "Group By",
      ungroupBy: "Ungroup By",
      ungroupAll: "Ungroup All",
      addToValues: "Add to Values",
      removeFromValues: "Remove from Values",
      addToLabels: "Add to Labels",
      removeFromLabels: "Remove from Labels",
      resetColumns: "Reset Columns",
      expandAll: "Expand All",
      collapseAll: "Collapse All",
      copy: "Copy",
      ctrlC: "Ctrl+C",
      ctrlX: "Ctrl+X",
      copyWithHeaders: "Copy with Headers",
      copyWithGroupHeaders: "Copy with Group Headers",
      cut: "Cut",
      paste: "Paste",
      ctrlV: "Ctrl+V",
      export: "Export",
      csvExport: "CSV Export",
      excelExport: "Excel Export",
      columnFilter: "Column Filter",
      columnChooser: "Column Chooser",
      sortAscending: "Sort Ascending",
      sortDescending: "Sort Descending",
      sortUnSort: "Unsort",
      sum: "Sum",
      first: "First",
      last: "Last",
      min: "Min",
      max: "Max",
      none: "None",
      count: "Count",
      avg: "Avg",
      filteredRows: "Filtered Rows",
      selectedRows: "Selected Rows",
      totalRows: "Total Rows",
      totalAndFilteredRows: "Total & Filtered Rows",
      more: "More",
      to: "to",
      of: "of",
      page: "Page",
      pageLastRowUnknown: "?",
      nextPage: "Next Page",
      lastPage: "Last Page",
      firstPage: "First Page",
      previousPage: "Previous Page",
      pageSizeSelectorLabel: "Rows per page",
      footerTotal: "Total",
      pivotColumnGroupTotals: "Pivot Group Totals",
      pivotChartAndPivotMode: "Pivot Chart & Pivot Mode",
      pivotChart: "Pivot Chart",
      chartRange: "Chart Range",
      columnChart: "Column Chart",
      groupedColumn: "Grouped Column",
      stackedColumn: "Stacked Column",
      normalizedColumn: "Normalized Column",
      barChart: "Bar Chart",
      groupedBar: "Grouped Bar",
      stackedBar: "Stacked Bar",
      normalizedBar: "Normalized Bar",
      pieChart: "Pie Chart",
      pie: "Pie",
      donut: "Donut",
      line: "Line",
      xyChart: "XY Chart",
      scatter: "Scatter",
      bubble: "Bubble",
      areaChart: "Area Chart",
      area: "Area",
      stackedArea: "Stacked Area",
      normalizedArea: "Normalized Area",
      histogramChart: "Histogram Chart",
      histogramFrequency: "Frequency",
      polarChart: "Polar Chart",
      radarLine: "Radar Line",
      radarArea: "Radar Area",
      nightingale: "Nightingale",
      radialColumn: "Radial Column",
      radialBar: "Radial Bar",
      statisticalChart: "Statistical Chart",
      boxPlot: "Box Plot",
      rangeBar: "Range Bar",
      rangeArea: "Range Area",
      hierarchicalChart: "Hierarchical Chart",
      treemap: "Treemap",
      sunburst: "Sunburst",
      specializedChart: "Specialized Chart",
      waterfall: "Waterfall",
      heatmap: "Heatmap",
      combinationChart: "Combination Chart",
      columnLineCombo: "Column Line Combo",
      AreaColumnCombo: "Area Column Combo",
      pivotChartTitle: "Pivot Chart",
      rangeChartTitle: "Range Chart",
      settings: "Settings",
      data: "Data",
      format: "Format",
      categories: "Categories",
      defaultCategory: "Default Category",
      series: "Series",
      switchCategorySeries: "Switch Category/Series",
      categoryValues: "Category Values",
      seriesLabels: "Series Labels",
      aggregate: "Aggregate",
      xyValues: "XY Values",
      paired: "Paired",
      axis: "Axis",
      xAxis: "X Axis",
      yAxis: "Y Axis",
      polarAxis: "Polar Axis",
      radiusAxis: "Radius Axis",
      navigator: "Navigator",
      zoom: "Zoom",
      animation: "Animation",
      crosshair: "Crosshair",
      color: "Color",
      thickness: "Thickness",
      preferredLength: "Preferred Length",
      xType: "X Type",
      axisType: "Axis Type",
      automatic: "Automatic",
      category: "Category",
      number: "Number",
      time: "Time",
      timeFormat: "Time Format",
      autoRotate: "Auto Rotate",
      labelRotation: "Label Rotation",
      circle: "Circle",
      polygon: "Polygon",
      orientation: "Orientation",
      fixed: "Fixed",
      parallel: "Parallel",
      perpendicular: "Perpendicular",
      radiusAxisPosition: "Radius Axis Position",
      ticks: "Ticks",
      gridLines: "Grid Lines",
      width: "Width",
      height: "Height",
      length: "Length",
      padding: "Padding",
      spacing: "Spacing",
      chart: "Chart",
      title: "Title",
      titlePlaceholder: "Chart Title",
      background: "Background",
      font: "Font",
      top: "Top",
      right: "Right",
      bottom: "Bottom",
      left: "Left",
      labels: "Labels",
      calloutLabels: "Callout Labels",
      sectorLabels: "Sector Labels",
      positionRatio: "Position Ratio",
      size: "Size",
      shape: "Shape",
      minSize: "Min Size",
      maxSize: "Max Size",
      legend: "Legend",
      position: "Position",
      markerSize: "Marker Size",
      markerStroke: "Marker Stroke",
      markerPadding: "Marker Padding",
      itemSpacing: "Item Spacing",
      itemPaddingX: "Item Padding X",
      itemPaddingY: "Item Padding Y",
      layoutHorizontalSpacing: "Horizontal Spacing"
    };
  }
  isFirstColumn(params) {
    let displayedColumns = params.columnApi.getAllDisplayedColumns();
    return displayedColumns[0] === params.column;
  }
  getRowStyle = (params) => {
    if (params.node.rowIndex % 2 === 1) {
      return { background: this.color1 };
    }
    return { background: this.color2 };
  };
  getRowStyleForSummary = (params) => {
    let color = null;
    let style = {
      background: params.node.rowIndex % 2 === 1 ? this.color1 : this.color2,
    } as any;
    if (params.data) {
      if (params.data.ExecutionStatus == "SUCCESS") {
        style.color = "green";
      } else if (params.data.ExecutionStatus == "FAILED") {
        style.color = "red";
      }
    }
    return style;
  }
   getLastUpdatedDateFormat(params) {
    // return date in MMM DD, YYYY, h:mm:ss A format
    return params.value ? moment(params.value).format('MMM DD, YYYY, h:mm:ss A') : '';
  }

  private applyDateTimeFormatting() {
    const dateTimeColumns = ["lastLoginDate", "createdOn","sqlLastLoginDateTime", "createdDateTime", "CreatedDateTime"];
    const dateColumns = ["updatedOn"];

    if (dateTimeColumns.includes(this.columnInfo.field)) {
      this.columnInfo.cellRenderer = function (params) {
        if (params.value !== null)
          return moment(params.value).format('MMM DD, YYYY, h:mm:ss A');
      };
    }
    if (dateColumns.includes(this.columnInfo.field)) {
      this.columnInfo.cellRenderer = function (params) {
        return moment(params.value).format('MMM DD, YYYY');
      };
    }

    if (this.columnInfo.field === "LastUpdatedDate") {
      //set the filter to agDateColumnFilter
      this.setupDateColumn(this.columnInfo, this.columnInfo.field, this.getLastUpdatedDateFormat);
    }
    if (this.columnInfo.field.toUpperCase() === "ACTION") {
      this.columnInfo.sortable = false;
      // this.columnInfo.suppressHeaderMenuButton = true;
      this.columnInfo.cellRenderer= function(params) {
        
            return '<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" >more_vert</a>'
        };
    }
  }
  GetColmnDefs(tableColumn: TableColumnDetails, isRequestFrom = null) {
    let columnDefs = [];
    for (const column of tableColumn.column) {
      if (column.headerName) {
        this.columnInfo = this.getColDef(column);
      } else {
        this.columnInfo = this.getDefaultColDef(column, column.dataType);
      }
            this.applyDateTimeFormatting();

      columnDefs.push(this.columnInfo);
    }
    return columnDefs;
  }

  AddCheckboxColumn(columnDefs: Array<ColDef>) {
    columnDefs.splice(0, 0, {
      headerName: "",
      field: "",
      sortable: false,
      filter: false,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      lockPosition: true,
      width: 50,
      maxWidth: 50,
      headerClass: "my-aggrid-header",
      suppressColumnsToolPanel: true,
    });
  }

  getColDef(tableColumn: TableColumn): ColDef {
    return {
      headerName: tableColumn.headerName,
      field: tableColumn.columnName,
      sortable: true,
      hide: tableColumn.hide ?? false,
      filter: true,
      type: tableColumn.type ?? '',
      cellStyle: tableColumn.cellStyle !== null ? { textAlign: tableColumn.cellStyle } : { textAlign: 'left' },
      pinned: tableColumn.pinned ?? false,
      maxWidth: tableColumn.maxWidth ?? 100,
      minWidth: tableColumn.minWidth ?? 100,
      headerClass: "my-aggrid-header",
      headerTooltip: tableColumn.headerName,
      tooltipField: tableColumn.columnName == "RepoCreationStatus" ? "" : tableColumn.columnName,
      cellEditor: tableColumn.cellEditor,
      cellEditorParams: tableColumn.cellEditorParams,
      // width:tableColumn.width
    };
  }

  getDefaultColDef(column: TableColumn, dataType: string): ColDef {
    return {
      headerName: column.columnName,
      field: column.columnName,
      sortable: true,
      filter: true,
      autoHeight: false,
      headerClass: "my-aggrid-header",
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      headerTooltip: column.columnName,
      tooltipField: column.columnName,
    };
  }

  /**
   * @description Get column definitions for user management ag-grid
   * @param tableColumn 
   * @param isRequestFrom 
   * @returns 
   */
  getReleaseDetails(tableColumn: TableColumnDetails, isRequestFrom = null) {
    // Initialize an empty array to store column definitions
    let columnDefs = [];

    // Iterate through each column in the tableColumn object
    for (const column of tableColumn.column) {
      //get column info
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Set filter parameters for text column filter
      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith']
      };

      this.customizeColdefs();

      // Customize column definition for "status" column
      if (this.columnInfo.field === "status") {
        // Set filter
        this.columnInfo.filter = column.filter;
        // Set filter parameters
        this.columnInfo.filterParams = {
          values: ['Approved', 'Unapproved', 'Revoked'],
          buttons: ['clear'],
          suppressSorting: true
        };
        // Set string formatter
        this.columnInfo.valueFormatter = this.stringFormatter;
      }

      // Remove filter for "ACTION" column
      if (this.columnInfo.field === "ACTION") {
        this.columnInfo.filter = false;
        this.columnInfo.headerComponent = AgHeaderRendererComponent;
        this.columnInfo.headerComponentParams = { tooltip: 'Select the action to be performed for the Release' };
        this.columnInfo.headerClass = "my-aggrid-header ag-header-cell-label-left";
      }

      // Customize column definition for "lastLoginDateTime" column
      if (this.columnInfo.field == "lastLoginDateTime") {
        this.setupDateColumn(this.columnInfo, "lastLoginDateTime", this.getLastLoginDateFormat);
      }

      // Customize column definition for "updatedOn" columns
      if (this.columnInfo.field == "CreatedDateTime" || this.columnInfo.field == "LastUpdatedDateTime") {
        this.setupDateColumn(this.columnInfo, this.columnInfo.field, this.getUpdatedOnDateFormat);
      }

      // Push the customized column definition to the columnDefs array
      columnDefs.push(this.columnInfo);
    }

    // Return the array of column definitions
    return columnDefs;
  }

  getRollbackColumnDetails(tableColumn: TableColumnDetails, isRequestFrom = null) {
    // Initialize an empty array to store column definitions
    let columnDefs = [];

    // Iterate through each column in the tableColumn object
    for (const column of tableColumn.column) {
      //get column info
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Set filter parameters for text column filter
      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'],
        suppressAndOrCondition: true
      };

      // Customize column definition for "ACTION" column
      this.addColumnSettingForAction();

      // Customize column definition for "status" column
      this.addColumnSettingForRollbackStatus(column);

      // Customize column definition for "status" column
      if (this.columnInfo.field === "status") {
        // Set filter
        this.columnInfo.filter = column.filter;
        // Set filter parameters
        this.columnInfo.filterParams = {
          values: ['Approved', 'Unapproved', 'Revoked'],
          buttons: ['clear'],
          suppressSorting: true
        };
        // Set string formatter
        this.columnInfo.valueFormatter = this.stringFormatter;
      }
      // Customize column definition for "failureComment" column
      this.addColumnSettingForFailureComment();

      // Customize column definition for "updatedOn" columns
      if (this.columnInfo.field == "rollbackInitiatedDatetime" || this.columnInfo.field == "rollbackCompletedDatetime") {
        this.setupDateColumn(this.columnInfo, this.columnInfo.field, this.getUpdatedOnDateFormat);
      }

      // Push the customized column definition to the columnDefs array
      columnDefs.push(this.columnInfo);
    }

    // Return the array of column definitions
    return columnDefs;
  }


  /**
   * Adds column settings for the failure comment.
   */
  private addColumnSettingForFailureComment() {
    if (this.columnInfo.field === "failureComment") {
      this.columnInfo.sortable = false;
      this.columnInfo.menuTabs = [];
      // Customizes cell content using a renderer function
      this.columnInfo.cellRenderer = (params) => {
        //Check if failure comment is not null or empty
        if (params.data.failureComment != null && params.data.failureComment != '') {
          return '<span class="info-icon align-middle" matTooltip="Click to view the failure reason"><i class="mr-0 material-icons" style="cursor:pointer" aria-hidden="true">info</i></span>';
        }
      };
      // Set tooltip field to null to avoid showing tooltip for failure comment column
      this.columnInfo.tooltipField = null;
      this.columnInfo.tooltipValueGetter = (params) => {
        return params.data.failureComment != null && params.data.failureComment != undefined ? this.getErrorMessageByCode(params.data.failureComment) : '';
      };
    }
  }

  /**
   * Adds column settings for the rollback status.
   * 
   * @param column - The TableColumn object.
   */
  private addColumnSettingForRollbackStatus(column: TableColumn) {
    if (this.columnInfo.field === "rollbackStatus") {
      // Set filter
      this.columnInfo.filter = column.filter;
      // Set filter parameters
      this.columnInfo.filterParams = {
        values: ['Not Started', 'In Progress', 'Completed', 'Failed'],
        buttons: ['clear'],
        suppressSorting: true
      };
      // Customizes cell content using a renderer function
      this.columnInfo.cellRenderer = (params) => {
        // Check the rollback status and return the corresponding HTML string
        // The HTML string contains a span element with a class that defines the color of the text
        if (params.data.rollbackStatus === this.constantsService.RollbackStatusNotStarted) { // Check if roll back status is Not Started
          return '<span class="RollbackNotStarted mat-chip-rollback">' + params.data.rollbackStatus + '</span>';
        } else if (params.data.rollbackStatus === this.constantsService.RollbackStatusInProgress) { // Check if roll back status is In Progress
          return '<span class="RollbackInProgress mat-chip-rollback">' + params.data.rollbackStatus + '</span>';
        } else if (params.data.rollbackStatus === this.constantsService.RollbackStatusCompleted) { // Check if roll back status is Completed
          return '<span class="RollbackCompleted mat-chip-rollback">' + params.data.rollbackStatus + '</span>';
        } else if (params.data.rollbackStatus === this.constantsService.RollbackStatusFailed) { // Check if roll back status is Failed
          return '<span class="RollbackFailed mat-chip-rollback">' + params.data.rollbackStatus + '</span>';
        }
      };
    }
  }

  /**
   * Adds column settings for the "ACTION" field.
   */
  private addColumnSettingForAction() {
    if (this.columnInfo.field.toUpperCase() === "ACTION") {
      this.columnInfo.sortable = true;
      this.columnInfo.suppressMenu = true;
      this.columnInfo.headerTooltip = "";
      // Customizes cell content using a renderer function
      const userAuthService = this.userAuthService; // capture reference for use in renderer
      this.columnInfo.cellRenderer = function (params) {
        const canEdit = userAuthService.checkPermission('TRIGGER.ROLLBACK.DATABASE');
        const isRollbackEnabled = (params.data.rollbackStatus === 'Not Started' || params.data.rollbackStatus === 'Failed') && canEdit;
        const rollbackClass = isRollbackEnabled ? 'RollbackEnable' : 'RollbackDisable';
        const pointerEvents = isRollbackEnabled ? '' : 'pointer-events: none; opacity: 0.5;';
        return `<span class="${rollbackClass}" style="${pointerEvents}">
          <span class="mdc-fab__icon material-icons-outlined rollBackIcon">autorenew</span>Rollback
        </span>`;
      };
    }
  }

  /**
     * Displays an error message based on the provided message code.
     * 
     * @param {string} messageCode - The code representing the error message.
     * @returns {void}
     */
  getErrorMessageByCode(errorMessage) {
    // Check the message code and set the error message accordingly
    // The error message is displayed using the Toastr service
    //Check if message code is equal to no environment mapping found
    if (errorMessage == "NO_ENVIRONMENT_MAPPING_FOUND") {
      errorMessage = this.constantsService.NO_ENVIRONMENT_MAPPING_FOUND;
    }
    //Check if message code is equal to no release service mapping found
    else if (errorMessage == "NO_RELEASE_SERVICE_MAPPING_FOUND") {
      errorMessage = this.constantsService.NO_RELEASE_SERVICE_MAPPING_FOUND;
    }
    //Check if message code is equal to no release found
    else if (errorMessage == "NO_RELEASE_FOUND") {
      errorMessage = this.constantsService.NO_RELEASE_FOUND;
    }
    //Check if message code is equal to rollback job already in progress
    else if (errorMessage == "ROLLBACK_JOB_ALREADY_IN_PROGRESS") {
      errorMessage = this.constantsService.ROLLBACK_JOB_ALREADY_IN_PROGRESS;
    }
    //Check if message code is equal to specified rollback version not found
    else if (errorMessage == "SPECIFIED_ROLLBACK_VERSION_NOT_FOUND") {
      errorMessage = this.constantsService.SPECIFIED_ROLLBACK_VERSION_NOT_FOUND;
    }
    //Check if message code is equal to rollback file not found
    else if (errorMessage == "ROLLBACK_FILE_NOT_FOUND") {
      errorMessage = this.constantsService.ROLLBACK_FILE_NOT_FOUND;
    }
    //Check if message code is equal to missing url path
    else if (errorMessage == "MISSING_URL_PATH") {
      errorMessage = this.constantsService.Missing_Url_Path;
    }
    //Check if message code is equal to missing client key or api key
    else if (errorMessage == "MISSING_CLIENTKEY_OR_APIKEY") {
      errorMessage = this.constantsService.Missing_ClientKey_Or_ApiKey;
    }
    //Check if message code is equal to invalid token
    else if (errorMessage == "INVALID_TOKEN") {
      errorMessage = this.constantsService.InvalidToken;
    }
    //Return the error message
    return errorMessage;
  }

  /*
  * @setting style for the row for rollback migration summary
  */
  getRollbackMigrationSummaryColumnDetails(tableColumn: TableColumnDetails, isRequestFrom = null) {
    // Initialize an empty array to store column definitions
    let columnDefs = [];

    // Iterate through each column in the tableColumn object
    for (const column of tableColumn.column) {
      //get column info
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Set filter parameters for text column filter
      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'],
        suppressAndOrCondition: true
      };

      // Customize column definition for "status" column
      if (this.columnInfo.field === "databaseType") {
        // Set filter
        this.columnInfo.filter = column.filter;
        // Set filter parameters
        this.columnInfo.filterParams = {
          values: ['Host', 'Tenant'],
          buttons: ['clear'],
          suppressSorting: true,
          suppressAndOrCondition: true
        };
      }

      // Customize column definition for "status" column
      this.addColumnSettingForStatus(column);

      // Customize column definition for "migrationName" column
      if (this.columnInfo.field === "migrationName" || this.columnInfo.field === "rollbackComment") {
        // Set auto height text to true for migrationName column
        this.columnInfo.autoHeight = true;
        // Set wrap text to true for migrationName column
        this.columnInfo.wrapText = true;
        // set cell class to wrap-text for migrationName column
        this.columnInfo.cellClass = 'wrap-text';
      }

      // Customize column definition for "rollbackComment" column
      this.addColumnSettingForRollbackComment();

      // Customize column definition for "rollbackCreatedDateTime" columns
      if (this.columnInfo.field == "rollbackCreatedDateTime") {
        this.setupDateColumn(this.columnInfo, this.columnInfo.field, this.getUpdatedOnDateFormat);
      }

      // Push the customized column definition to the columnDefs array
      columnDefs.push(this.columnInfo);
    }

    // Return the array of column definitions
    return columnDefs;
  }

  /**
   * Adds a column setting for the rollback comment.
   * If the column field is "rollbackComment", it sets a cell renderer function that displays the rollback comment
   * only if the status of the data is "RollbackMigrationStatusFailed".
   */
  private addColumnSettingForRollbackComment() {
    if (this.columnInfo.field === "rollbackComment") {
      // Set filter for rollbackComment column
      this.columnInfo.cellRenderer = (params) => {
        // Check if the status of the data is "RollbackMigrationStatusFailed"
        if (params.data.status === this.constantsService.RollbackMigrationStatusFailed) {
          return params.data.rollbackComment;
        } else {
          return '';
        }
      };
    }
  }

  /**
   * Adds column settings for the specified column.
   * @param column - The column to add settings for.
   */
  private addColumnSettingForStatus(column: TableColumn) {
    if (this.columnInfo.field === "status") {
      // Set filter
      this.columnInfo.filter = column.filter;
      // Set filter parameters
      this.columnInfo.filterParams = {
        values: ['Success', 'Failed'],
        buttons: ['clear'],
        suppressAndOrCondition: true,
        suppressSorting: true
      };
      // Set string formatter for status column
      this.columnInfo.cellRenderer = (params) => {
        if (params.data.status === this.constantsService.RollbackMigrationStatusSuccess) { // Check if status is Success
          return '<span class="StatusSuccess">' + params.data.status + '</span>';
        } else if (params.data.status === this.constantsService.RollbackMigrationStatusFailed) { // Check if status is Failed
          return '<span class="StatusFailed">' + params.data.status + '</span>';
        }
      };
    }
  }

  /**
   * @description Set up date column
   * @param columnInfo 
   * @param field 
   * @param dateFormatFunction 
   */
  setupDateColumn(columnInfo: any, field: string, dateFormatFunction: (params: any) => string) {
  if (columnInfo.field === field) {
    columnInfo.filter = 'agDateColumnFilter';
    columnInfo.cellRenderer = dateFormatFunction;
    columnInfo.filterParams = {
      buttons: ['clear'],
      filterOptions: ['equals'], 
      defaultOption: 'equals',
      suppressAndOrCondition: true 
    };
    columnInfo.tooltipField = null;
    columnInfo.tooltipValueGetter = dateFormatFunction;
  }
}


  /**
  * @description string formatter
  * @param params 
  * @returns 
  */
  stringFormatter(params) {
    // return string in title case
    return params.value.slice(0, 1).toUpperCase() + params.value.slice(1).toLowerCase();
  }

  /**
   * @description Get date format
   * @param value 
   * @returns 
   */
  getUpdatedOnDateFormat(params) {
    return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm:ss') : '';
  }

  /**
   * 
   * @param params @description get last login date format
   * @returns 
   */
  getLastLoginDateFormat(params) {
    return params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm:ss') : '';
  }

  /**
   * @description Get column definition and filter for ag-grid
   * @param column 
   * @param isRequestFrom 
   */
  getColumnInfoAndFilter(column, isRequestFrom) {
    // Check if headerName is defined in the column
    if (column.headerName) {
      // If headerName is defined, get column definition using getColDef function
      this.columnInfo = this.getColDef(column);
    } else {
      // If headerName is not defined, get default column definition using getDefaultColDef function
      this.columnInfo = this.getDefaultColDef(column, column.dataType);
    }
    // If isRequestFrom is not provided, add text column filter
    if (!isRequestFrom) {
      this.columnInfo.filter = "agTextColumnFilter";
    }
  }

  // Defines column behavior and appearance for a grid
  colDefsAction() {
    // Disables sorting for this column
    this.columnInfo.sortable = false;
    // Suppresses the default column menu
    this.columnInfo.suppressMenu = true;
    // Sets an empty string as the header tooltip, effectively disabling it
    this.columnInfo.headerTooltip = "";
    // Customizes cell content using a renderer function
    this.columnInfo.cellRenderer = function (params) {
      // Returns HTML string for a Material Design icon button to be used in the cell
      return '<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" style="margin-top: 8px;">more_vert</a>';
    };
  }

  // Configures column definitions specifically for displaying reciprocation status
  colDefsRreciprocationStatus() {
    // Disables sorting functionality for the column
    this.columnInfo.sortable = false;
    // Suppresses the default column menu to prevent it from appearing
    this.columnInfo.suppressMenu = true;
    // Customizes cell style with specific padding and text alignment
    this.columnInfo.cellStyle = { padding: '2px', textAlign: 'center' };
    // Specifies a custom renderer named 'progressRender' for cell content
    this.columnInfo.cellRenderer = 'progressRender';
    // Assigns a custom header component for the column
    this.columnInfo.headerComponent = AgHeaderRendererComponent;
    // Sets an empty string as the header tooltip, effectively disabling it
    this.columnInfo.headerTooltip = "";
    // Provides parameters for the header component, including a tooltip
    this.columnInfo.headerComponentParams = { tooltip: 'Click/hover on each icon to show the status of release activities' };
  }

  // Configures column definitions for displaying deployment status
  colDefsDeploymentStatus() {
    // Assigns a custom header component for the column
    this.columnInfo.headerComponent = AgHeaderRendererComponent;
    // Sets an empty string as the header tooltip, effectively disabling it
    this.columnInfo.headerTooltip = "";
    // Provides parameters for the header component, including a tooltip with a descriptive message
    this.columnInfo.headerComponentParams = { tooltip: 'View the status of Release deployment on the environment selected' };
    // Specifies a custom renderer named 'AgChipRendererComponent' for cell content, likely to display status as chips/tags
    this.columnInfo.cellRenderer = AgChipRendererComponent;
  }
  colDefsReleaseType() {
    // Assigns a custom header component for the column
    this.columnInfo.headerComponent = AgHeaderRendererComponent;
    // Sets an empty string as the header tooltip, effectively disabling it
    this.columnInfo.headerTooltip = "";
    // Provides parameters for the header component, including a tooltip with a descriptive message
    this.columnInfo.headerComponentParams = {};
    // Specifies a custom renderer named 'AgChipRendererComponent' for cell content, likely to display status as chips/tags
    this.columnInfo.cellRenderer = AgChipRendererComponent;
  }

  // Configures column definitions for displaying database synchronization status
  colDefsSyncDbStatus() {
    // Assigns a custom header component for enhanced header rendering
    this.columnInfo.headerComponent = AgHeaderRendererComponent;
    // Sets an empty string as the header tooltip, effectively disabling it
    this.columnInfo.headerTooltip = "";
    // Provides parameters for the header component, including a tooltip that explains the column's purpose
    this.columnInfo.headerComponentParams = { tooltip: 'View the status of data sync from Prod to Staging' };
    // Specifies a custom renderer for the cell content, likely to display status as visual chips or tags
    this.columnInfo.cellRenderer = AgChipRendererComponent;
  }

  // Configures column definitions for the "Release Name" column
  colDefsReleaseName() {
    // Disables sorting for this column to prevent users from changing the order of releases
    this.columnInfo.sortable = false;
    // Suppresses the default column menu to simplify the UI and prevent additional actions on this column
    this.columnInfo.suppressMenu = true;

    // Enhanced cellRenderer that preserves existing link functionality AND adds info icon
    if (this.userAuthService.checkPermission('MANAGE.RELEASE.MODIFY') || this.userAuthService.checkPermission('MANAGE.RELEASE.STATUS.VIEW')) {
      // User has permission - show as clickable link with optional info icon
      this.columnInfo.cellRenderer = (params) => {
        const releaseName = params.value || '';
        const parentReleaseName = params.data?.ParentReleaseName || '';
        const releaseId = params.data?.Id || '';
        const cellId = `release-cell-${params.node.rowIndex}`;

        // Add click event handler after DOM is ready
        setTimeout(() => {
          const linkElement = document.getElementById(`link-${cellId}`);
          if (linkElement) {
            linkElement.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              // Dispatch custom event that your component listens to
              window.dispatchEvent(new CustomEvent('releaseNameClicked', {
                detail: {
                  releaseId: releaseId,
                  releaseName: releaseName,
                  data: params.data
                }
              }));
            });
          }
        }, 0);

        // Check if there's a parent release name
        if (parentReleaseName && parentReleaseName.trim().length > 0) {
          // Show link with info icon
          return `
         <div style="display: flex; align-items: center; width: 100%; gap: 4px;">
          <a id="link-${cellId}" 
             class="ag-link" 
             style="color: rgb(0, 123, 255); text-decoration: none; cursor: pointer; min-width: 0; flex: 1; display: flex; align-items: center;">
            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; ">${releaseName}</span>
            <span class="info-icon" matTooltip="Parent: ${parentReleaseName}" style="margin-left: 2px; display: flex; align-items: center; flex-shrink: 0;">
              <span style="font-family: 'Material Icons'; font-size: 18px; color: rgba(0, 0, 0, 0.54); cursor: pointer;">info</span>
            </span>
          </a>
         </div>
        `;
        } else {
          // Just show link (existing functionality)
          return `<a id="link-${cellId}" 
                   class="ag-link" 
                   style="color: rgb(0, 123, 255); text-decoration: none; cursor: pointer;"
                   href="javascript:void(0);">${releaseName}</a>`;
        }
      };
    } else {
      // User doesn't have permission - show as text with optional info icon
      this.columnInfo.cellRenderer = (params) => {
        const releaseName = params.value || '';
        const parentReleaseName = params.data?.ParentReleaseName || '';

        if (parentReleaseName && parentReleaseName.trim().length > 0) {
          // Show text with info icon
          return `
          <div style="display: flex; align-items: center; width: 100%; gap: 4px;">
            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; ">${releaseName}</span>
            <span class="info-icon" matTooltip="Parent: ${parentReleaseName}" style="margin-left: 2px; display: flex; align-items: center; flex-shrink: 0;">
              <span style="font-family: 'Material Icons'; font-size: 18px; color: rgba(0, 0, 0, 0.54); cursor: pointer;">info</span>
            </span>
          </div>
        `;
        } else {
          // Just show text (existing functionality)
          return `<span>${releaseName}</span>`;
        }
      };
    }
    // Add tooltip specifically for the info icon using ag-Grid's tooltip system
    this.columnInfo.tooltipValueGetter = (params) => {
      const parentReleaseName = params.data?.ParentReleaseName || '';
      if (parentReleaseName && parentReleaseName.trim().length > 0) {
        return `Parent: ${parentReleaseName}`;
      }
      return null; // No tooltip when no parent release
    };

    // Clear any conflicting tooltip settings
    this.columnInfo.tooltipField = null;
  }
  customizeColdefs() {
    // Customize column definition for "ACTION" column
    if (this.columnInfo.field.toUpperCase() === "ACTION") {
      this.colDefsAction();
    }

    if (this.columnInfo.field.toUpperCase() === "REPOCREATIONSTATUS") {
      this.colDefsRreciprocationStatus();
    }

    if (this.columnInfo.field.toUpperCase() === "DEPLOYMENTSTATUS") {
      this.colDefsDeploymentStatus();
    }
    if (this.columnInfo.field.toUpperCase() === "RELEASETYPE") {
      this.colDefsReleaseType();
    }
    if (this.columnInfo.field.toUpperCase() === "SYNCDBSTATUS") {
      this.colDefsSyncDbStatus();
    }

    if (this.columnInfo.field.toUpperCase() === "RELEASENAME") {
      this.colDefsReleaseName();
    }
  }

  GetUserManagementColmnDefs(tableColumn: TableColumnDetails, isRequestFrom = null) {
    // Initialize an empty array to store column definitions
    let columnDefs = [];
    // Iterate through each column in the tableColumn object
    for (const column of tableColumn.column) {
      //get column info
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Set filter parameters for text column filter
      let supportedFilterOptions = ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'];

      // Customize filter options for "displayRoleNames" column
      if (this.columnInfo.field == "displayRoleNames") {
        supportedFilterOptions = ['contains', 'notContains', 'equals', 'notEqual'];
      }

      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: supportedFilterOptions
      };

      // Customize column definition for "ACTION" column
      if (this.columnInfo.field.toUpperCase() === "ACTION") {
        this.columnInfo.sortable = false;
        //this.columnInfo.suppressHeaderMenuButton = true;
        this.columnInfo.cellRenderer = function (params) {
          return '<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" >more_vert</a>';
        };
      }

      // Customize column definition for "status" column
      if (this.columnInfo.field === "status") {
        // Set filter
        this.columnInfo.filter = column.filter;
        // Set filter parameters
        this.columnInfo.filterParams = {
          values: ['Approved', 'Unapproved'],
          buttons: ['clear'],
          suppressSorting: true
        };
        // Set string formatter
        this.columnInfo.valueFormatter = this.stringFormatter;
      }

      // Remove filter for "ACTION" column
      if (this.columnInfo.field === "ACTION") {
        this.columnInfo.filter = false;
      }

     // Customize column definition for "lastLoginDateTime" column
      if(this.columnInfo.field == "lastLoginDateTime"){
        this.setupDateColumn(this.columnInfo, "lastLoginDateTime", this.getLastLoginDateFormat);
      }

      // Customize column definition for "lastupdated" columns
      if (this.columnInfo.field == "updatedOn") {
        this.setupDateColumn(this.columnInfo, "updatedOn", this.getUpdatedOnDateFormat);
      }

      // Push the customized column definition to the columnDefs array
      columnDefs.push(this.columnInfo);
    }

    // Return the array of column definitions
    return columnDefs;
  }
  /**
* @description Get column definitions for role management ag-grid
* @param tableColumn 
* @param isRequestFrom 
* @returns 
*/
  GetRoleManagementColmnDefs(tableColumn: TableColumnDetails, isRequestFrom = null) {
    // Initialize an empty array to store column definitions
    let columnDefs = [];

    // Iterate through each column in the tableColumn object
    for (const column of tableColumn.column) {
      //get column info
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Set filter parameters for text column filter
      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith']
      };

      // Customize column definition for "ACTION" column
      if (this.columnInfo.field.toUpperCase() === "ACTION") {
        // Set sortable and suppressHeaderMenuButton to false
        this.columnInfo.sortable = false;
        ///this.columnInfo.suppressHeaderMenuButton = true;
        // Set cell renderer for "ACTION" column
        this.columnInfo.cellRenderer = function (params) {
          return '<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" >more_vert</a>';
        };
      }

      // Customize column definition for "status" column
      if (this.columnInfo.field && this.columnInfo.field.toLowerCase() === "isactive") {
        // Set filter parameters
        // Set filter
        this.columnInfo.filter = column.filter;
        // Set filter parameters
        this.columnInfo.filterParams = {
          values: [true, false],
          buttons: ['clear'],
          suppressSorting: true,
          valueFormatter:(params)=> (typeof params === 'object' ? params.value : params) === 'true' ? 'Active' : 'Inactive'
        };
        this.columnInfo.cellRenderer = this.formatStatusBasedOnFlag;
        // set tool tip value getter
        this.columnInfo.tooltipValueGetter = this.formatStatusBasedOnFlag;
      }

      // Remove filter for "ACTION" column
      if (this.columnInfo.field === "ACTION") {
        this.columnInfo.filter = false;
      }

      // Push the customized column definition to the columnDefs array
      columnDefs.push(this.columnInfo);
    }

    // Return the array of column definitions
    return columnDefs;
  }
  GetServiceConfigurationColmnDefs(tableColumn: TableColumnDetails, isRequestFrom = null) {
  let columnDefs: ColDef[] = [];

  for (const column of tableColumn.column) {
    // Use your config properties directly
    const colDef: ColDef = {
      headerName: column.headerName,
      field: column.columnName,
      filter: column.filter,
      filterParams: column.filterParams,
      flex: column.flex, // <-- ADD THIS LINE
      
      floatingFilter: false,
      sortable: true,
      hide: column.hide ?? false,
      type: column.type ?? '',
      cellStyle: typeof column.cellStyle === 'object' ? column.cellStyle : { textAlign: 'left' },
      pinned: column.pinned ?? false,
      maxWidth: column.maxWidth,
      minWidth: column.minWidth,
      width: column.width,
      headerClass: "my-aggrid-header",
      headerTooltip: column.headerName,
      tooltipField: column.columnName,
      cellRenderer: column.cellRenderer,
    };

    // Override tooltip for Status, API, Web, and Last Updated By columns
    if (colDef.field) {
      const field = colDef.field.toLowerCase();
      if (field === 'status' && column.tooltipValueGetter) {
        colDef.tooltipValueGetter = column.tooltipValueGetter;
        delete colDef.tooltipField;
      }
      if (field === 'hasapi' && column.tooltipValueGetter) {
        colDef.tooltipValueGetter = column.tooltipValueGetter;
        delete colDef.tooltipField;
      }
      if (field === 'hasweb' && column.tooltipValueGetter) {
        colDef.tooltipValueGetter = column.tooltipValueGetter;
        delete colDef.tooltipField;
      }
       if (field === 'lastupdateddatetime' && column.tooltipValueGetter) {
         colDef.tooltipValueGetter = column.tooltipValueGetter;
         delete colDef.tooltipField;
       }
    }

    // Copy tooltipValueGetter if present in input config
    if (column.tooltipValueGetter) {
      colDef.tooltipValueGetter = column.tooltipValueGetter;
    }

    // Special handling for ACTION column (cog menu)
    // if (colDef.field && colDef.field.toUpperCase() === "ACTION") {
    //   colDef.sortable = false;
    //   colDef.filter = false;
    //   colDef.suppressMenu = true;
    //   colDef.width = column.width ?? 40;
    //   colDef.cellStyle = typeof column.cellStyle === 'object'
    //     ? column.cellStyle
    //     : { textAlign: typeof column.cellStyle === 'string' ? column.cellStyle : 'center' };
    //   colDef.cellRenderer = function () {
    //     return `<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" style="margin-top: 8px;">more_vert</a>`;
    //   };
    // }
    if (colDef.field && colDef.field.toUpperCase() === "ACTION") {
        colDef.sortable = false; colDef.filter = false;
        //this.columnInfo.suppressHeaderMenuButton = true;
      colDef.cellRenderer = function (params) {
        return '<a class="material-icons mdc-icon-button mdc-card__action mdc-card__action--icon list-template-hover" >more_vert</a>';
      };
    }

    columnDefs.push(colDef);
  }

  return columnDefs;
}
  /**
 * @description Format status based on flag
 * @param params
 * @returns 
 */
 formatStatusBasedOnFlag(params) {
  const value = params && typeof params === 'object' ? params.value : params;
  return value == true ? 'Active' : 'Inactive';
}

  /**
   * @description Get column definitions for Release Manifest Status ag-grid
   * @param tableColumn 
   * @returns 
   */
  GetReleaseManifestStatusColDefs(tableColumn: TableColumnDetails, isRequestFrom = null) {
    let columnDefs: ColDef[] = [];
    for (const column of tableColumn.column) {
      this.getColumnInfoAndFilter(column, isRequestFrom);
      // Custom logic for status column
      if (this.columnInfo.field === 'pipelineStatus') {
        this.columnInfo.filter = true;
        this.columnInfo.filterValueGetter = params => params.data.status;
        this.columnInfo.cellRenderer = AgChipRendererComponent;
        this.columnInfo.headerComponent = AgHeaderRendererComponent;
      }
      // Custom logic for triggeredOn column: date time formatting
      if (this.columnInfo.field === 'triggerDateTime') {
      this.columnInfo.cellRenderer = (params) => {
        return params.value ? moment(params.value).format('MMM DD, YYYY, h:mm:ss A') : '';
      };
      this.columnInfo.filter = 'agDateColumnFilter';
      this.columnInfo.filterParams = {
        buttons: ['clear'],
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true 
      };
      this.columnInfo.tooltipValueGetter = (params) => {
        return params.value ? moment(params.value).format('MMM DD, YYYY, h:mm:ss A') : '';
      };
    }
      columnDefs.push(this.columnInfo);
    }
    return columnDefs;
  }
}
